import { useState } from "react";
import { toast } from "sonner";
import { GraduationCap, Check, Briefcase } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";

export const YEAR_GROUPS = ["Primary (KS2)", "Year 7", "Year 8", "Year 9", "Year 10 (GCSE)", "Year 11 (GCSE)", "Year 12 (A level)", "Year 13 (A level)", "University"];

export const YearGroupPicker = ({ onSaved }) => {
  const { user, setUser } = useAuth();
  const [busy, setBusy] = useState("");
  const pick = async (yg) => {
    setBusy(yg);
    try { const { data } = await api.put("/profile/year-group", { year_group: yg }); setUser(data); toast.success(`Set to ${yg}. Packs, marking and Ask Daybreyk will pitch to this level`); onSaved?.(); }
    catch (e) { toast.error(formatApiError(e)); }
    setBusy("");
  };
  return (
    <div data-testid="year-group-picker" className="flex flex-wrap gap-2">
      {YEAR_GROUPS.map((yg) => {
        const on = user?.year_group === yg;
        return (
          <button key={yg} data-testid={`year-group-${yg.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`} onClick={() => pick(yg)} disabled={Boolean(busy)}
            className={`inline-flex items-center gap-1.5 rounded-full border px-3.5 py-2 text-sm font-semibold transition-colors ${on ? "border-gold-400 bg-gold-400 text-black" : "border-slate-200 bg-surface text-slate-700 hover:border-gold-400"} disabled:opacity-60`}>
            {on && <Check className="w-3.5 h-3.5" />}{yg}
          </button>
        );
      })}
    </div>
  );
};

export const YearGroupCard = () => {
  const { user } = useAuth();
  return (
    <article data-testid="year-group-card" className="card min-w-0 p-5 sm:p-6">
      <div className="flex items-center gap-3">
        <span className="grid place-items-center w-10 h-10 shrink-0 rounded-xl bg-gold-400/15 text-gold-300"><GraduationCap className="w-5 h-5" /></span>
        <div><h3 className="font-semibold text-slate-900">Year group</h3><p className="text-sm text-slate-500">{user?.year_group ? `Currently ${user.year_group}.` : "Not set yet."} Revision packs, AI marking and Ask Daybreyk pitch to this level.</p></div>
      </div>
      <div className="mt-4"><YearGroupPicker /></div>
    </article>
  );
};

export const JobRolePicker = ({ onSaved }) => {
  const { user, setUser } = useAuth();
  const [v, setV] = useState(user?.job_role || "");
  const [busy, setBusy] = useState(false);
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { const { data } = await api.put("/profile/job-role", { job_role: v.trim() }); setUser(data); toast.success("Job role saved"); onSaved?.(); }
    catch (err) { toast.error(formatApiError(err)); }
    setBusy(false);
  };
  return (
    <form data-testid="job-role-form" onSubmit={save} className="flex gap-2">
      <input data-testid="job-role-input" value={v} onChange={(e) => setV(e.target.value)} minLength={2} maxLength={60} required placeholder="e.g. Marketing manager"
        className="h-11 flex-1 min-w-0 rounded-xl border border-slate-200 bg-surface px-3 text-sm text-slate-900 outline-none focus:border-gold-400" />
      <button data-testid="job-role-save" disabled={busy || v.trim().length < 2} className="h-11 rounded-xl bg-gold-400 px-4 text-sm font-bold text-black disabled:opacity-50">Save</button>
    </form>
  );
};

export const JobRoleCard = () => {
  const { user } = useAuth();
  return (
    <article data-testid="job-role-card" className="card min-w-0 p-5 sm:p-6">
      <div className="flex items-center gap-3">
        <span className="grid place-items-center w-10 h-10 shrink-0 rounded-xl bg-gold-400/15 text-gold-300"><Briefcase className="w-5 h-5" /></span>
        <div><h3 className="font-semibold text-slate-900">Job role</h3><p className="text-sm text-slate-500">{user?.job_role ? `Currently ${user.job_role}.` : "Not set yet."} Briefs, learning packs and Ask Daybreyk use this for context.</p></div>
      </div>
      <div className="mt-4"><JobRolePicker /></div>
    </article>
  );
};

export const YearGroupPrompt = () => {
  const { user } = useAuth();
  const corp = user?.edition === "corporate";
  if (!user || (corp ? user.job_role : user.year_group)) return null;
  return (
    <section data-testid="year-group-prompt" className="card p-5 sm:p-6 !border-gold-400/60 animate-rise">
      <p className="eyebrow flex items-center gap-2">{corp ? <Briefcase className="w-4 h-4" /> : <GraduationCap className="w-4 h-4" />}Quick one</p>
      <h2 className="mt-1 font-display text-3xl text-slate-900">{corp ? "What's your job role?" : "What year are you in?"}</h2>
      <p className="mt-1 mb-4 text-sm text-slate-600">{corp ? "So your briefs and Ask Daybreyk fit your work. You can change it in Settings." : "So revision packs, marking and Ask Daybreyk pitch to your level. You can change it in Settings."}</p>
      {corp ? <JobRolePicker /> : <YearGroupPicker />}
    </section>
  );
};
